import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { FOOTBALL_ONBOARDING_SELECT_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import type {
  claimInteruptedCardsFromPackMutation,
  claimInteruptedCardsFromPackMutationVariables,
} from './__generated__/usePackOpeningInterruptedSnackbar.graphql';

const CLAIM_INTERUPTED_CARDS_FROM_PACK_MUTATION = gql`
  mutation claimInteruptedCardsFromPackMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        claimablePacks(sport: $sport) {
          id
        }
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimInteruptedCardsFromPackMutation,
  claimInteruptedCardsFromPackMutationVariables
>;

export const usePackOpeningInterruptedSnackbar = () => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const location = useLocation();
  const { isIosApp } = useIsMobileApp();

  const { showNotification } = useSnackNotificationContext();

  const [claimInteruptedCardsFromPack] = useMutation(
    CLAIM_INTERUPTED_CARDS_FROM_PACK_MUTATION
  );

  const shouldClaim =
    !isIosApp &&
    currentUser?.claimablePacks.length &&
    // TODO: figure out why ConfigQuery is refetched after starter pack is claimed
    !matchPath(location.pathname, FOOTBALL_ONBOARDING_SELECT_COMPETITION);

  useEffect(() => {
    if (!shouldClaim) {
      return;
    }
    if (currentUser?.claimablePacks.length) {
      (async () => {
        await Promise.all(
          currentUser.claimablePacks.map(async pack => {
            await claimInteruptedCardsFromPack({
              variables: {
                sport,
                input: {
                  packId: pack.id,
                  chosenCardSlugs: pack.cards.map(card => card.slug),
                },
              },
            });
          })
        );
        showNotification('packOpeningInterrupted');
      })();
    }
  }, [
    currentUser?.claimablePacks,
    claimInteruptedCardsFromPack,
    shouldClaim,
    showNotification,
    sport,
  ]);

  return null;
};
